import { useContext, useEffect } from "react";
import { useForm } from "react-final-form";
import { getIn } from "final-form";
import {
  UnderwritingFormContext,
  UnderwritingFormContextType,
} from "../context/UnderwritingFormContext";
import FinancialDocsCard from "./FinancialDocsCard";
import {
  formatMisPeriod,
  getMisPeriodLabel,
  isFyClassificationJobProcessing,
} from "./revenueHelper";

/** Form field holding the MIS documents. Read as `data.misReports` by the API. */
export const MIS_REPORTS_FIELD_NAME = "misReports";

type Props = {
  newUploadedDocIds: number[];
  setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
  deleteDocIds: number[];
  setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const MisReportRow = ({
  newUploadedDocIds,
  setNewUploadedDocIds,
  deleteDocIds,
  setDeleteDocIds,
}: Props) => {
  const { getState } = useForm();
  const {
    misClassificationJob,
    clearMisClassificationJob,
    clearAllMisClassificationData,
  } = useContext(UnderwritingFormContext) as UnderwritingFormContextType;

  const misJob = misClassificationJob?.job ?? null;
  // The API computes the window per request (it advances on the 15th), so prefer
  // its period; the local label is only a pre-fetch fallback.
  const periodLabel =
    formatMisPeriod(misClassificationJob?.requiredPeriod?.period) ??
    getMisPeriodLabel();
  // MIS nests its per-doc tags under `classificationResult.classificationTags`,
  // where a financial year stores a bare array.
  const misDocClassifications = Array.isArray(misJob?.classificationResult)
    ? misJob?.classificationResult
    : misJob?.classificationResult?.classificationTags ?? null;

  // Deleting the documents a complaint was about should take the complaint with
  // them, rather than leaving it up until the next classification push. Mirrors
  // the same effect on RevenueRow.
  useEffect(() => {
    if (!misDocClassifications || deleteDocIds.length === 0) return;

    const files: { uploadedFile?: { docId?: number } }[] =
      getIn(getState().values, MIS_REPORTS_FIELD_NAME) ?? [];
    const hasNoUploadedFiles = !files.some((f) => !!f?.uploadedFile?.docId);
    if (hasNoUploadedFiles) {
      clearAllMisClassificationData();
      return;
    }

    const errorDocIds = misDocClassifications
      .filter(
        (entry: { docId: number; error: any }) =>
          entry.error && Object.keys(entry.error).length > 0
      )
      .map((entry: { docId: number }) => entry.docId);
    if (errorDocIds.length === 0) return;
    const allErrorFilesDeleted = errorDocIds.every((id: number) =>
      deleteDocIds.includes(id)
    );
    if (allErrorFilesDeleted) {
      clearMisClassificationJob();
    }
  }, [deleteDocIds, misDocClassifications]);

  return (
    <FinancialDocsCard
      title={`MIS (${periodLabel})`}
      description="Ensure your MIS covers your P&L."
      uploadBtnText="Upload MIS"
      filesFieldName={MIS_REPORTS_FIELD_NAME}
      section="mis"
      classificationResult={misDocClassifications}
      classificationErrorDetails={misJob?.classificationErrorDetails}
      isProcessing={isFyClassificationJobProcessing(misJob)}
      missingDocsError={{
        title: "MIS not uploaded.",
        action: `We use this to assess your monthly business performance. Upload your MIS for ${periodLabel}.`,
      }}
      newUploadedDocIds={newUploadedDocIds}
      setNewUploadedDocIds={setNewUploadedDocIds}
      deleteDocIds={deleteDocIds}
      setDeleteDocIds={setDeleteDocIds}
    />
  );
};

export default MisReportRow;
